import { useRef } from "react";
import cx from "classnames";
import { faPrescription, faTag } from "@fortawesome/pro-regular-svg-icons";

import LinkButton from "common/components/LinkButton";
import CatalogImage from "common/components/CatalogImage";
import Icon from "common/components/Icon";

import TextChip from "client/common/components/content/TextChip";
import { urls } from "client/routes";
import { sendSelectItem, useSendPostHogProductClick } from "client/analytics/utils";
import { ProductListSource } from "client/analytics/types";

import { ProductSearchResult } from "../types";

import styles from "../styles/ProductSearchMobile.module.less";

type ProductSearchMobileResultV2Props = { result: ProductSearchResult };

// v2 redesigned mobile card. An exact copy of the v1 result for now; the redesign lands here in a
// later PR, switched in behind the PRODUCT_SEARCH_CARDS_V2 flag.
const ProductSearchMobileResultV2 = ({ result }: ProductSearchMobileResultV2Props) => {
  const resultRef = useRef<HTMLDivElement>(null);
  const sendPostHogProductClick = useSendPostHogProductClick();

  const hasPrescriptionLabel = Boolean(result.patient_prescription_label);
  const isFeatured =
    result.product_line.is_featured || result.product_line.is_loyalty_program_featured;
  const handleClick = () => {
    sendSelectItem(result, ProductListSource.SearchResults);
    sendPostHogProductClick({
      source: ProductListSource.SearchResults,
      id: result.id,
      product_line_id: result.product_line.id,
    });
  };

  return (
    <LinkButton
      dest={urls.products.detail({
        params: { id: result.product_line.id },
        search: { product: result.id },
      })}
      className={styles.result}
      onClick={handleClick}
    >
      {(hasPrescriptionLabel || isFeatured) && (
        <div
          className={cx(
            styles.background,
            hasPrescriptionLabel ? styles.prescribed : styles.featured,
          )}
        />
      )}
      <CatalogImage
        image={result.main_image}
        size="thumbnail"
        alt={result.name}
        className={styles.image}
      />
      <div className={styles.item} ref={resultRef}>
        {(hasPrescriptionLabel || isFeatured) &&
          (hasPrescriptionLabel ? (
            <TextChip size="s" variant="info">
              {result.patient_prescription_label}
            </TextChip>
          ) : (
            <TextChip size="s" variant="highlight">
              Your Vet&apos;s Choice
            </TextChip>
          ))}
        {result.product_line.prescription_required && (
          <Icon icon={faPrescription} className={styles.prescription} />
        )}
        <p className={styles.name}>{result.full_name}</p>
        {result.promotion_label && (
          <div className={styles.promotionText}>
            <Icon icon={faTag} /> {result.promotion_label}
          </div>
        )}
      </div>
    </LinkButton>
  );
};

export default ProductSearchMobileResultV2;
